import React, { useState, useEffect } from 'react';
import { Modal, View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import theme from '@/config/theme';
import { AdminLogger } from '@/utils/logger';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ptBR } from 'date-fns/locale';
import { databaseService } from '@/services/databaseService';
import APP_CONSTANTS from '@/config/constants';
import { globalState } from '@/state/globalState';
import TemplatePicker from './TemplatePicker';

interface Props {
  visible: boolean;
  onClose: () => void;
  filters: any;
  setFilters: (f: any) => void;
}

// CSS customizado adaptado (parte reduzida)
const injectStyles = () => {
  if (document.getElementById('dashboard-filter-datepicker-css')) return;
  const style = document.createElement('style');
  style.id = 'dashboard-filter-datepicker-css';
  style.innerHTML = `
    .react-datepicker {
      border: none;
      border-radius: ${theme.borderRadius.md}px;
      background: ${theme.colors.background.primary};
      font-family: ${theme.fontFamily?.regular || 'Roboto, sans-serif'};
      box-shadow: 0 2px 10px rgba(0,0,0,.08);
    }
    .react-datepicker__header {
      background: ${theme.colors.background.primary};
      border-bottom: 1px solid ${theme.colors.border.light};
    }
    .react-datepicker__day {
      color: ${theme.colors.text.primary};
    }
    .react-datepicker__day--selected, .react-datepicker__day--keyboard-selected {
      background: ${theme.colors.primary.default};
      color: #fff;
    }
  `;
  document.head.appendChild(style);
};

export default function DashboardFilter({ visible, onClose, filters, setFilters }: Props) {
  const log = AdminLogger.Filter;
  const [localFilters, setLocalFilters] = useState(filters);

  // Lista de templates disponíveis
  const [templateOptions, setTemplateOptions] = useState<Array<{ value: string | null; label: string }>>([
    { value: null, label: 'Todos' },
  ]);

  // Carregar templates quando o modal ficar visível
  useEffect(() => {
    const loadTemplates = async () => {
      if (!visible) return; // evita chamadas desnecessárias
      try {
        const empresaId = globalState.user.profile.get()?.empresa_id;
        if (!empresaId) return;

        const templates = await databaseService.listItems<any>(
          APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
          { empresa_id: empresaId },
        );

        const opts = templates
          .filter((t: any) => !t.retired_at)
          .map((t: any) => ({ value: t.id, label: t.nome }));
        setTemplateOptions([{ value: null, label: 'Todos' }, ...opts]);
      } catch (err) {
        log.error('Erro ao carregar templates', err);
      }
    };

    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Injetar CSS customizado apenas uma vez
  useEffect(() => {
    injectStyles();
  }, []);

  const apply = () => {
    log.info('Aplicando filtros', localFilters);
    setFilters(localFilters);
    onClose();
  };
  const cancel = () => {
    log.info('Cancelado filtro');
    onClose();
  };

  const statusOptions: Array<{ value: any; label: string }> = [
    { value: null, label: 'Todos' },
    { value: 'in_progress', label: 'Em andamento' },
    { value: 'done', label: 'Concluído' },
  ];

  return (
    <Modal visible={visible} animationType="fade" transparent>
      <View style={styles.overlay}>
        <View style={styles.container}>
          <Text style={styles.title}>Filtros</Text>

          {/* Date pickers inline */}
          <Text style={styles.label}>Data Início</Text>
          <DatePicker
            selected={localFilters.startDate ? new Date(localFilters.startDate) : null}
            onChange={(d: Date | null) => {
              log.info('Alterou startDate', d);
              setLocalFilters({ ...localFilters, startDate: d ? d.setHours(0, 0, 0, 0) : null })
            }}
            dateFormat="dd/MM/yyyy"
            locale={ptBR}
            placeholderText="Selecione..."
            preventOpenOnFocus
            className="react-datepicker-ignore-onclickoutside"
          />

          <Text style={[styles.label, { marginTop: 8 }]}>Data Fim</Text>
          <DatePicker
            selected={localFilters.endDate ? new Date(localFilters.endDate) : null}
            onChange={(d: Date | null) => {
              log.info('Alterou endDate', d);
              setLocalFilters({ ...localFilters, endDate: d ? d.setHours(23, 59, 59, 999) : null })
            }}
            dateFormat="dd/MM/yyyy"
            locale={ptBR}
            placeholderText="Selecione..."
            preventOpenOnFocus
            className="react-datepicker-ignore-onclickoutside"
          />

          {/* Status */}
          <Text style={[styles.label, { marginTop: 12 }]}>Status</Text>
          <View style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginBottom: 8 }}>
            {statusOptions.map((opt, idx) => (
              <TouchableOpacity
                key={idx}
                style={[styles.badge, localFilters.status === opt.value && styles.badgeActive]}
                onPress={() => {
                  log.info('Alterou status', opt.value);
                  setLocalFilters({ ...localFilters, status: opt.value })
                }}
              >
                <Text style={{ color: '#fff' }}>{opt.label}</Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Template */}
          <Text style={[styles.label, { marginTop: 12 }]}>Template</Text>
          <TemplatePicker
            options={templateOptions}
            value={localFilters.templateId}
            onChange={(val) => {
              log.info('Alterou templateId', val);
              setLocalFilters({ ...localFilters, templateId: val });
            }}
          />

          <TouchableOpacity style={[styles.button, { backgroundColor: theme.colors.util.warning }]} onPress={() => {
            const now = new Date();
            const first = new Date(now.getFullYear(), now.getMonth(), 1).setHours(0, 0, 0, 0);
            const last = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
            const defaultFilters = { ...localFilters, startDate: first, endDate: last, status: null, templateId: null };
            setLocalFilters(defaultFilters);
            setFilters(defaultFilters);
            log.info('Filtros resetados');
            onClose();
          }}>
            <Text style={styles.buttonText}>Resetar</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.button} onPress={apply}>
            <Text style={styles.buttonText}>Aplicar</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.button, { backgroundColor: theme.colors.util.error }]} onPress={cancel}>
            <Text style={styles.buttonText}>Cancelar</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: theme.colors.overlay.semi,
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    width: 400,
    maxWidth: '90%',
    padding: 16,
    borderRadius: 12,
    backgroundColor: theme.colors.background.primary,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
    color: theme.colors.text.primary,
  },
  badge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: theme.colors.primary.default,
    marginRight: 8,
    marginTop: 4,
  },
  badgeActive: {
    backgroundColor: theme.colors.primary.dark,
  },
  button: {
    marginTop: 16,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: theme.colors.primary.default,
    alignItems: 'center',
  },
  buttonText: {
    color: '#fff',
    fontWeight: 'bold',
  },
  label: {
    color: theme.colors.text.primary,
    marginBottom: 4,
  },
  dateButton: {
    backgroundColor: theme.colors.background.secondary,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  dateButtonText: {
    color: theme.colors.text.primary,
  },
});
